import React, { FC, useEffect, useState } from 'react';
import Loadable from '@app/utils/Loadable';
import { OrganizationalUser } from '@app/domain/userManagement/organizationUsers';
import { PaginationConfig } from 'antd/lib/table';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import { ExtendedColumnProps, TableImportantText, TableStandardText } from '@app/components/tables/Table';
import { pluralize } from '@app/utils/stringUtils';
import { getColumnTextSearchProps } from '@app/components/tableFilters/textSearch/byTextSearchFilterCreator';
import TableOfCards from '@app/components/tables/TableOfCards';
import getColumnMultiValueArrayFieldSelectProps from '@app/components/tableFilters/multiValue/byArrayFieldFilterCreator';
import { useLoadableSearchFilter } from '@app/hooks/useSearchFilter';
import OrganizationalUserActionsMenu, {
  OrganizationalUserActionsPermissions,
} from '@app/components/userManagement/organizationUsersTable/OrganizationalUserActionsMenu';
import styled from '@emotion/styled';
import GlobalRolesModal from '@app/components/userManagement/organizationUsersTable/GlobalRolesModal';
import UserPermissionsModal from '@app/components/userManagement/organizationUsersTable/UserPermissionsModal';
import useModalContext from '@app/hooks/useModalContext';
import ModalAppContext from '@app/ModalAppContext';
import { AllKnoxers } from '@app/domain/userManagement/knoxers';
import { KnoxerIdentityProviders } from '@app/domain/userManagement/identityProviders';
import { ifNotProp } from 'styled-tools';
import { compare } from '@app/utils/comparatorUtils';
import useControlledModal from '@app/hooks/useControlledModal';
import { UserRoleType, UserRoleTypeServerResponse } from '@app/domain/userManagement/roleTypes';
import useLoadable from '@app/hooks/loadable/useLoadable';
import SelectedUsersRolesModal from '@app/components/userManagement/organizationUsersTable/SelectedUsersRolesModal';
import { RolesOfUsersSaveProgress, UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import Button from '@app/components/Button';
import { CaptionStartTransparentBlack600 } from '@app/components/Text';
import { TableRowSelection } from 'antd/lib/table/interface';
import { applyColumnFilters, ColumnFilters } from '@app/utils/tableFilterUtils';
import { distinctValues } from '@app/utils/arrayUtils';
import SelectedUsersChips from '@app/components/userManagement/organizationUsersTable/SelectedUsersChips';

interface OrganizationUsersTableProps {
  id: string;
  organizationId: string;
  dataTestId?: string;
  usersLoadable: Loadable<OrganizationalUser[]>;
  knoxers: AllKnoxers;
  organizationIdentityProviders: KnoxerIdentityProviders[];
  canSuspendUserFromOrganization: boolean;
  canKillUserSessions: boolean;
  canEditUserPermissions: boolean;
  // Provided only for a user allowed to edit global roles
  globalWebAppRoleTypes?: Loadable<UserRoleType[]>;
  className?: string;
  suspendUserFromOrganization(tenantUserId: string, userName: string | null): void;
  killUserSessions(tenantUserId: string, userName: string | null): void;
  onUserUpdated(organizationalUser: OrganizationalUser): void;
  getOrganizationRoleTypes(organizationId: string): Promise<UserRoleTypeServerResponse[]>;
  // Provided only where several users' roles may be edited at once
  saveRolesOfUsers?(
    organizationalUsers: OrganizationalUser[],
    change: UpdateWebUserRolesRequest,
    progress: RolesOfUsersSaveProgress,
  ): Promise<void>;
}

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

const OrganizationUsersTable: FC<OrganizationUsersTableProps> = ({
  id,
  organizationId,
  dataTestId = id,
  usersLoadable,
  knoxers,
  organizationIdentityProviders,
  canSuspendUserFromOrganization,
  canKillUserSessions,
  canEditUserPermissions,
  globalWebAppRoleTypes,
  suspendUserFromOrganization,
  killUserSessions,
  onUserUpdated,
  getOrganizationRoleTypes,
  saveRolesOfUsers,
  className,
}) => {
  const modalContext = useModalContext();
  const [filteredData, searchTerm, setSearchTerm] = useLoadableSearchFilter(usersLoadable, {
    exactMatches(user) {
      return [user.tenantUserId];
    },
    partialMatches(user) {
      return [user.name, user.email, user.phoneNumber];
    },
  });

  const [organizationUserRoleType] = useLoadable(async (): Promise<UserRoleType[]> => {
    const organizationRoleTypesServerResponse = await getOrganizationRoleTypes(organizationId);
    return organizationRoleTypesServerResponse.sort((roleType1, roleType2) => roleType1.id.localeCompare(roleType2.id));
  }, [organizationId]);

  const [userPermissionsModalUser, setUserPermissionsModalUser] = useState<OrganizationalUser | null>(null);
  const showUserPermissionsModal = (organizationalUser: OrganizationalUser): void => {
    setUserPermissionsModalUser(organizationalUser);
  };

  const [globalRolesModalUser, setGlobalRolesModalUser] = useState<OrganizationalUser | null>(null);

  // One filter state drives both antd and the matching, since antd's own filtered rows miss the search box's changes
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const columns = createColumns(
    filteredData.result ?? [],
    {
      canSuspendUserFromOrganization,
      canKillUserSessions,
      canEditGlobalRoles: !!globalWebAppRoleTypes,
    },
    suspendUserFromOrganization,
    killUserSessions,
    showUserPermissionsModal,
    setGlobalRolesModalUser,
    knoxers,
    organizationIdentityProviders,
  ).map((column) => (column.onFilter ? { ...column, filteredValue: columnFilters[String(column.key)] ?? [] } : column));
  const matchingUsers = applyColumnFilters(filteredData.result ?? [], columns, columnFilters);
  const matchingTenantUserIds = new Set(matchingUsers.map((user) => user.tenantUserId));

  const [selectedTenantUserIds, setSelectedTenantUserIds] = useState<string[]>([]);
  // Else a user who leaves the list would come back already selected
  const loadedUsers = usersLoadable.result;
  useEffect(() => {
    if (!loadedUsers) {
      return;
    }

    const loadedTenantUserIds = new Set(loadedUsers.map((user) => user.tenantUserId));
    setSelectedTenantUserIds((currentTenantUserIds) => {
      const stillLoadedTenantUserIds = currentTenantUserIds.filter((tenantUserId) => loadedTenantUserIds.has(tenantUserId));
      return stillLoadedTenantUserIds.length === currentTenantUserIds.length ? currentTenantUserIds : stillLoadedTenantUserIds;
    });
  }, [loadedUsers]);
  const selectedTenantUserIdSet = new Set(selectedTenantUserIds);
  const selectedUsers = (loadedUsers ?? [])
    .filter((user) => selectedTenantUserIdSet.has(user.tenantUserId))
    .sort(compare.byStringField((user) => user.name));
  const hiddenSelectedUsersCount = selectedUsers.filter((user) => !matchingTenantUserIds.has(user.tenantUserId)).length;
  const hasUnselectedMatchingUsers = matchingUsers.some((user) => !selectedTenantUserIdSet.has(user.tenantUserId));

  const selectAllMatchingUsers = (): void =>
    setSelectedTenantUserIds(distinctValues([...selectedTenantUserIds, ...matchingUsers.map((user) => user.tenantUserId)]));
  const clearSelection = (): void => setSelectedTenantUserIds([]);
  const deselectUser = (tenantUserId: string): void =>
    setSelectedTenantUserIds((currentTenantUserIds) => currentTenantUserIds.filter((selectedId) => selectedId !== tenantUserId));

  const rowSelection: TableRowSelection<OrganizationalUser> = {
    selectedRowKeys: selectedTenantUserIds,
    onChange: (newSelectedTenantUserIds): void => setSelectedTenantUserIds(newSelectedTenantUserIds as string[]),
    hideDefaultSelections: true,
    selections: [
      { key: 'select-all-matching', text: `Select all ${matchingUsers.length} matching`, onSelect: selectAllMatchingUsers },
      { key: 'clear-selection', text: 'Clear selection', onSelect: clearSelection },
    ],
  };

  useControlledModal(
    !!userPermissionsModalUser,
    (onDone) => {
      if (!userPermissionsModalUser) {
        return <div />;
      }

      return (
        <ModalAppContext {...modalContext}>
          <UserPermissionsModal
            organizationId={organizationId}
            organizationalUser={userPermissionsModalUser}
            userRoleTypes={organizationUserRoleType}
            canEdit={canEditUserPermissions}
            onUserUpdated={onUserUpdated}
            onDone={(): void => onDone(true)}
          />
        </ModalAppContext>
      );
    },
    (): void => {
      setUserPermissionsModalUser(null);
    },
    {},
    [organizationId, userPermissionsModalUser, organizationUserRoleType],
  );

  useControlledModal(
    !!globalRolesModalUser && !!globalWebAppRoleTypes,
    (onDone) => {
      if (!globalRolesModalUser || !globalWebAppRoleTypes) {
        return <div />;
      }

      return (
        <ModalAppContext {...modalContext}>
          <GlobalRolesModal
            organizationalUser={globalRolesModalUser}
            globalWebAppRoleTypes={globalWebAppRoleTypes}
            onUserUpdated={onUserUpdated}
            onDone={(): void => onDone(true)}
          />
        </ModalAppContext>
      );
    },
    (): void => {
      setGlobalRolesModalUser(null);
    },
    {},
    [globalRolesModalUser, globalWebAppRoleTypes],
  );

  const [rolesModalUsers, setRolesModalUsers] = useState<OrganizationalUser[] | null>(null);
  useControlledModal(
    !!rolesModalUsers && !!saveRolesOfUsers,
    (onDone) => {
      if (!rolesModalUsers || !saveRolesOfUsers) {
        return <div />;
      }

      return (
        <ModalAppContext {...modalContext}>
          <SelectedUsersRolesModal
            organizationalUsers={rolesModalUsers}
            userRoleTypes={organizationUserRoleType}
            saveRolesOfUsers={saveRolesOfUsers}
            onDone={(): void => onDone(true)}
          />
        </ModalAppContext>
      );
    },
    (): void => {
      setRolesModalUsers(null);
    },
    // A dismissed modal would leave its batches running unseen
    { maskClosable: false, keyboard: false },
    [rolesModalUsers, organizationUserRoleType],
  );

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const paginationConfig: PaginationConfig = {
    position: 'both',
    pageSize,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    // Else a page size that fits every row hides the size changer too, with no way back to a smaller page
    hideOnSinglePage: matchingUsers.length <= Number(PAGE_SIZE_OPTIONS[0]),
  };

  return (
    <div className={className}>
      <InputBox appearance='line' colorScheme='secondary'>
        <NakedFormInput
          name={`${id}-search`}
          dataTestId={`${dataTestId}-search`}
          type='text'
          colorScheme='primary'
          placeholderStyle='onlyWhenEmpty'
          heightType='thick'
          clearable
          placeholder='🔍 Search for Id / Name / Email / Phone number'
          value={searchTerm}
          onChange={(newValue): void => setSearchTerm(newValue ?? '')}
          autoFocus
        />
      </InputBox>
      {saveRolesOfUsers && (
        <SelectionActionsLine>
          <SelectedUsersChips
            id={`${id}-selected-users`}
            dataTestId={`${dataTestId}-selected-users`}
            selectedUsers={selectedUsers}
            deselectUser={deselectUser}
          />
          <SelectionActions>
            {hasUnselectedMatchingUsers && matchingUsers.length > pageSize && (
              <Button
                id={`${id}-select-all-matching`}
                dataTestId={`${dataTestId}-select-all-matching`}
                appearance='text'
                colorScheme='primary'
                onClick={selectAllMatchingUsers}
              >
                SELECT ALL {matchingUsers.length} MATCHING
              </Button>
            )}
            <CaptionStartTransparentBlack600.span data-testid={`${dataTestId}-selected-count`}>
              {selectedUsers.length} selected
              {hiddenSelectedUsersCount > 0 && ` (${hiddenSelectedUsersCount} hidden by the current filters)`}
            </CaptionStartTransparentBlack600.span>
            {selectedUsers.length > 0 && (
              <Button
                id={`${id}-clear-selection`}
                dataTestId={`${dataTestId}-clear-selection`}
                appearance='text'
                colorScheme='primary'
                onClick={clearSelection}
              >
                CLEAR
              </Button>
            )}
            <Button
              id={`${id}-edit-roles`}
              dataTestId={`${dataTestId}-edit-roles`}
              appearance='text'
              colorScheme='primary'
              disabled={!selectedUsers.length}
              onClick={(): void =>
                selectedUsers.length === 1 ? showUserPermissionsModal(selectedUsers[0]) : setRolesModalUsers(selectedUsers)
              }
            >
              EDIT ROLES
            </Button>
          </SelectionActions>
        </SelectionActionsLine>
      )}
      <TableOfCards
        id={id}
        data-testid={dataTestId}
        columns={columns}
        dataSource={filteredData.result ?? []}
        loading={!filteredData.isResolved()}
        pagination={paginationConfig}
        onChange={(pagination, filters): void => {
          setColumnFilters(filters as ColumnFilters);
          setPageSize(pagination.pageSize ?? DEFAULT_PAGE_SIZE);
        }}
        rowSelection={saveRolesOfUsers ? rowSelection : undefined}
        rowKey={(organizationalUser: OrganizationalUser): string => organizationalUser.tenantUserId}
        scroll={{ x: true }}
        bodyStyle={{ overflowX: 'auto' }}
      />
    </div>
  );
};

export default OrganizationUsersTable;

function renderSSOIdentityProvidersOfKnoxer(
  record: OrganizationalUser,
  knoxerId: string,
  knoxerIdentityProviders: KnoxerIdentityProviders[],
): React.ReactNode {
  const ssoIdsOfKnoxer = record.ssoUserIds.find((knoxerIdentityProviders) => knoxerIdentityProviders.knoxerId === knoxerId);
  const identityProvidersNamesOfKnoxer =
    knoxerIdentityProviders
      .find((knoxerIdentityProvider) => knoxerIdentityProvider.knoxerId === knoxerId)
      ?.identityProviders.map((value) => value.name) ?? [];

  return ssoIdsOfKnoxer?.identities
    .map((userIdentityProviderData) => ({
      ...userIdentityProviderData,
      isIdentityProviderDefinedForOrganization: identityProvidersNamesOfKnoxer.includes(
        userIdentityProviderData.identityProviderName,
      ),
    }))
    .sort(compare.byField((first) => first.isIdentityProviderDefinedForOrganization, compare.booleans()))
    .map((userIdentityProviderData) => (
      <UserIdentity
        key={userIdentityProviderData.identityProviderName}
        isIdentityProviderDefinedForOrganization={userIdentityProviderData.isIdentityProviderDefinedForOrganization}
      >{`${userIdentityProviderData.id} | ${userIdentityProviderData.identityProviderName}`}</UserIdentity>
    ));
}

function createColumns(
  data: OrganizationalUser[],
  actionsPermissions: OrganizationalUserActionsPermissions,
  suspendUserFromOrganization: OrganizationUsersTableProps['suspendUserFromOrganization'],
  killUserSessions: OrganizationUsersTableProps['killUserSessions'],
  showUserPermissionsModal: (organizationalUser: OrganizationalUser) => void,
  showGlobalRolesModal: (organizationalUser: OrganizationalUser) => void,
  knoxers: AllKnoxers,
  knoxerIdentityProviders: KnoxerIdentityProviders[],
): ExtendedColumnProps<OrganizationalUser>[] {
  const renderPermissionsChip = (text: any, record: OrganizationalUser): React.ReactNode => {
    let chipText = 'Set permissions';
    if (record?.allActiveAppRoleIds?.length !== 0) {
      chipText = `${record.allActiveAppRoleIds.length} ${pluralize('Permission', record.allActiveAppRoleIds.length)}`;
    }
    return (
      <PermissionsButton
        data-testid={`permissions-chip-${record.tenantUserId}`}
        onClick={(): void => showUserPermissionsModal(record)}
      >
        {chipText}
      </PermissionsButton>
    );
  };

  return [
    {
      title: <div data-testid='lblName'>Name</div>,
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: compare.byStringField((record) => record.name),
      render: (text: any, record: OrganizationalUser): React.ReactNode => <TableImportantText>{record.name}</TableImportantText>,
      ...getColumnTextSearchProps((record) => record.name),
    },
    {
      title: <div data-testid='lblEmail'>{knoxers.emailKnoxer.name} Knoxer</div>,
      dataIndex: 'emailKnoxer',
      key: 'emailKnoxer',
      sorter: compare.byStringField((record) => record.email),
      render: (text: any, record: OrganizationalUser): React.ReactNode => (
        <div>
          {record.email && <TableStandardText>{record.email} | nsKnox Email Auth</TableStandardText>}
          {renderSSOIdentityProvidersOfKnoxer(record, knoxers.emailKnoxer.id, knoxerIdentityProviders)}
        </div>
      ),
      ...getColumnTextSearchProps((record) => record.email),
    },
    {
      title: <div data-testid='lblPhoneNumber'>{knoxers.phoneKnoxer.name} Knoxer</div>,
      dataIndex: 'phoneKnoxer',
      key: 'phoneKnoxer',
      sorter: compare.byStringField((record) => record.phoneNumber),
      render: (text: any, record: OrganizationalUser): React.ReactNode => (
        <div>
          {record.phoneNumber && <TableStandardText>{record.phoneNumber} | nsKnox Phone Auth</TableStandardText>}
          {renderSSOIdentityProvidersOfKnoxer(record, knoxers.phoneKnoxer.id, knoxerIdentityProviders)}
        </div>
      ),
      ...getColumnTextSearchProps((record) => record.phoneNumber),
    },
    {
      title: <div data-testid='lblPermissions'>Permissions</div>,
      dataIndex: 'permissions',
      key: 'permissions',
      sorter: (record1, record2): number => record1.allActiveAppRoleIds.length - record2.allActiveAppRoleIds.length,
      render: renderPermissionsChip,
      ...getColumnMultiValueArrayFieldSelectProps<OrganizationalUser>(data, (record) => record.allActiveAppRoleIds),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: '60px',
      hidden: Object.values(actionsPermissions).every((permission) => permission === false),
      render: (text: any, record: OrganizationalUser): React.ReactNode => {
        return (
          <OrganizationalUserActionsMenu
            tenantUserId={record.tenantUserId}
            permissions={actionsPermissions}
            suspendUserFromOrganization={(): void => suspendUserFromOrganization(record.tenantUserId, record.name)}
            killUserSessions={(): void => killUserSessions(record.tenantUserId, record.name)}
            editGlobalRoles={(): void => showGlobalRolesModal(record)}
          />
        );
      },
    },
  ];
}

const SelectionActionsLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

// Wraps as one, so a narrow line never leaves a lone action on a row of its own
const SelectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const PermissionsButton = styled.div`
  width: 128px;
  border-radius: 12px;
  background-color: var(--accent-blue-600);
  text-align: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--white);
`;

const UserIdentity = styled(TableStandardText)<{ isIdentityProviderDefinedForOrganization: boolean }>`
  ${ifNotProp(
    'isIdentityProviderDefinedForOrganization',
    `
  color: var(--transparent-black-400);
  `,
  )}
`;
